import React from "react";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { ExecutionTimeBadge } from "./ExecutionTimeBadge";
import { Eye, Coins } from "@phosphor-icons/react";
import { dateIsRecent } from "@/zapi-admin/shared/lib/dates";
import { Costs } from "@/zapi-admin/pages/AiLogsPage/ui/Costs";

type TLogRow = {
  executionTimeMs?: number | null;
  label?: string;
  id: number;
  errors?: string | null;
  cacheHit: boolean | null;
  createdAt: Date;
  costs?: {
    meta?: { source?: string };
    cost?: { total?: number; source?: string } | null;
    tokens?: { inputTokens?: number; outputTokens?: number; cacheReadTokens?: number };
  } | null;
};

type TAiLogGroupTableProps = {
  rows: Array<TLogRow>;
  onDetailClick: (logId: number) => void;
};

const isError = (row: TLogRow) =>
  !!row.errors || (row.executionTimeMs === null && !row.cacheHit && !dateIsRecent(row.createdAt));

export function AiLogGroupTable({ rows, onDetailClick }: TAiLogGroupTableProps) {
  return (
    <Card className="py-0 rounded-[10px] border-zinc-150 border-[1px] bg-white overflow-hidden">
      <Table className="p-[12px] text-zinc-500 text-[10px]">
        <TableHeader className="p-[12px] text-zinc-500 text-[10px] border-b-[1px]">
          <TableRow>
            <TableHead className="w-[20px] text-zinc-600 text-[14px] capitalize">№</TableHead>
            <TableHead className="min-w-[30px] text-zinc-600 text-[14px] capitalize">Execution Time</TableHead>
            <TableHead className="min-w-[20px] text-zinc-600 text-[14px] capitalize">Cost</TableHead>
            <TableHead className="min-w-[120px] text-zinc-600 text-[14px] capitalize">Label</TableHead>
            <TableHead className="w-[40px] text-zinc-600 text-[14px] capitalize">View</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, idx) => {
            const isRowError = isError(row);
            return (
              <TableRow
                key={idx}
                className={
                  "transition cursor-default " +
                  (isRowError ? "bg-[#FFF0F0A6] hover:bg-innoveraRed-50" : "hover:bg-zinc-50")
                }
              >
                <TableCell className="font-medium w-[20px] text-center">{idx + 1}</TableCell>
                <TableCell className="font-medium w-[30px] ">
                  <ExecutionTimeBadge executionTime={row.executionTimeMs ?? null} />
                </TableCell>
                <TableCell className="font-medium w-[20px] ">
                  {(row.costs || row.cacheHit !== null) && (
                    <Tooltip>
                      <TooltipTrigger>
                        <AppButton
                          appearance="text"
                          size="icon"
                          className={
                            "rounded-[6px] border-[1px] text-innoveraZinc-900 w-[24px] h-[24px] group-hover:border-innoveraZinc-100 group-hover:bg-innoveraZinc-100 " +
                            (isRowError
                              ? "border-innoveraZinc-100 bg-innoveraZinc-100 group-hover:hover:bg-innoveraZinc-150"
                              : "border-innoveraZinc-150 group-hover:hover:bg-innoveraZinc-150")
                          }
                          title="Cost details"
                        >
                          <Coins
                            style={{
                              width: "12px",
                              height: "12px"
                            }}
                          />
                        </AppButton>
                      </TooltipTrigger>
                      <TooltipContent>
                        <Costs
                          source={row.costs?.meta?.source ?? ""}
                          cost={row.costs?.cost?.total || 0}
                          inputTokens={row.costs?.tokens?.inputTokens ?? 0}
                          cachedInputTokens={row.costs?.tokens?.cacheReadTokens ?? 0}
                          outputTokens={row.costs?.tokens?.outputTokens ?? 0}
                          sourceCost={row.costs?.cost?.source}
                          cacheHit={row.cacheHit}
                        />
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  <FigmaTypography variant="label-2" className="text-zinc-900">
                    {row.label || "-"}
                  </FigmaTypography>
                </TableCell>
                <TableCell className="font-medium w-[40px] text-center">
                  <button
                    type="button"
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-zinc-300 transition"
                    onClick={() => onDetailClick(row.id)}
                  >
                    <Eye size={20} className="text-zinc-600" />
                  </button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}
